import { Button, DialogActions, DialogContent, DialogHeading, Typography } from 'omakase';
import { DisplayDate, DisplayTime } from '@tock/time';
import { Grid } from 'ui';
import {
  IconCalendar,
  IconCheckmarkCircle,
  IconClock,
  IconPeopleFamily,
  IconPlate,
} from '@tock/icons';
import { LocalDate } from 'js-joda';
import { compareLocalDate } from 'utils/timeUtils';
import { core } from '@tock/coretypes';
import {
  successTitle,
  waitlistMetaContainer,
  waitlistMetaItem,
} from 'components/business/waitlist-dialog/waitlistSuccessPageCssDefs';
import Anchor from 'components/Anchor';
import React from 'react';

type Props = {
  isAuthenticated: boolean;
  waitlistEntry?: core.IWaitlist;
  onClose(): void;
};

/**
 * Maybe starting a little util functional for display of waitlist time ranges.
 * We know that the dates may be different across ranges but the times will be
 * the same so we just want the first set when we display the time range.
 */
const getDesiredTimeRange = (timeRanges: core.IWaitlistDesiredDateTimeRange[]) => {
  const startDateTime = timeRanges?.[0]?.startDateTime;
  const endDateTime = timeRanges?.[0]?.endDateTime;

  if (!startDateTime || !endDateTime) {
    return undefined;
  }

  return { startDateTime, endDateTime };
};

export const WaitlistSuccessPage = ({ isAuthenticated, onClose, waitlistEntry }: Props) => {
  if (!waitlistEntry) {
    return null;
  }
  const desiredDateTimeRange = waitlistEntry.desiredDateTimeRange ?? [];
  const desiredTimeRange = getDesiredTimeRange(desiredDateTimeRange);
  const { business, desiredSize, ticketTypeName } = waitlistEntry;

  const renderDates = () => (
    <Typography variant="body01">
      {(waitlistEntry.desiredDate ?? [])
        .map((dateString) => LocalDate.parse(dateString))
        .filter((date): date is LocalDate => date != null)
        .sort(compareLocalDate)
        .map((date, index, arr) => {
          const isLastDate = index === arr.length - 1;
          return (
            <React.Fragment key={index}>
              <DisplayDate dateValue={date || ''} displayMonthAs="short" />
              {!isLastDate && ', '}
            </React.Fragment>
          );
        })}
    </Typography>
  );

  const singleTime =
    desiredTimeRange?.startDateTime === desiredTimeRange?.endDateTime
      ? desiredTimeRange?.startDateTime
      : null;

  return (
    <>
      <DialogHeading onClose={onClose}>
        <span css={successTitle}>
          Notify set <IconCheckmarkCircle />
        </span>
      </DialogHeading>
      <DialogContent>
        <Typography>
          Thanks for your interest! We’ll contact you if a spot opens up that matches the following:
        </Typography>
        <div css={waitlistMetaContainer}>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <Typography variant="body01Strong">{business?.name}</Typography>
            </Grid>
            <Grid item xs={12}>
              <ul>
                {desiredSize && (
                  <li css={waitlistMetaItem}>
                    <IconPeopleFamily />
                    <Typography variant="body01">
                      {desiredSize} {desiredSize > 1 ? 'guests' : 'guest'}
                    </Typography>
                  </li>
                )}
                <li css={waitlistMetaItem}>
                  <IconCalendar />
                  {renderDates()}
                </li>
                <li css={waitlistMetaItem}>
                  <IconPlate />
                  <Typography variant="body01">{ticketTypeName ?? 'Any experience'}</Typography>
                </li>
                {desiredTimeRange && (
                  <li css={waitlistMetaItem}>
                    <IconClock />
                    <Typography variant="body01">
                      {singleTime ? (
                        <DisplayTime timeValue={singleTime} />
                      ) : (
                        <>
                          <DisplayTime timeValue={desiredTimeRange.startDateTime} />
                          {' - '}
                          <DisplayTime timeValue={desiredTimeRange.endDateTime} />
                        </>
                      )}
                    </Typography>
                  </li>
                )}
              </ul>
            </Grid>
          </Grid>
        </div>
      </DialogContent>
      <DialogActions>
        {isAuthenticated ? (
          <Button
            fullWidth
            variant="ghost"
            size="medium"
            component={Anchor}
            href="/profile/reservations/waitlist"
          >
            View all requests
          </Button>
        ) : null}
        <Button fullWidth variant="primary" size="medium" onClick={onClose}>
          Done
        </Button>
      </DialogActions>
    </>
  );
};
